/* prettier-ignore */

import dayjs from 'dayjs';

/** Historical default grid start of the legacy planner (09:00). */
export const DEFAULT_GRID_START_HOUR = 9;

/**
 * Hour 0 ("00:00") is this grid's TERMINAL slot (midnight at the end of the
 * day), so it is never a valid grid START hour. Valid start hours are 1-23.
 */
const MIN_GRID_START_HOUR = 1;
const MAX_GRID_START_HOUR = 23;
const MIDNIGHT_END_HOUR = 24;

/**
 * Resolves the effective grid start hour of a stored route (HRS-3326):
 * min(declared route start hour, earliest stop start hour across all days),
 * falling back to 09:00 when neither source is parseable.
 *
 * Accepts the encodings seen at its call sites:
 * - point `start`: "HH:mm" string (isRefactorRoute routes) or numeric hour (raw kiosk routes)
 * - declared hour: dayjs (`routeConfig.startHourFirstDay`), "HH:mm" string or numeric hour (stored day `lowerHour`)
 *
 * `type: 'add'` slots and unparseable/out-of-range values are ignored.
 */
export function getEffectiveStartHour(days: any[], declaredStartHour: unknown): number {
  const candidates: number[] = [];

  const declaredHour = parseGridStartHour(declaredStartHour);
  if (declaredHour !== null)
    candidates.push(declaredHour);

  (days || []).forEach((day: any) => {
    (day?.points || []).forEach((item: any) => {
      if (item?.type === 'add')
        return;

      const stopHour = parseGridStartHour(item?.start);
      if (stopHour !== null)
        candidates.push(stopHour);
    });
  });

  if (candidates.length === 0)
    return DEFAULT_GRID_START_HOUR;

  return Math.min(...candidates);
}

/**
 * Resolves the grid start hour the given days are ACTUALLY gridded on: the
 * hour of the first slot (point or add) of each day, minimized across days,
 * falling back to 09:00. Post-merge bugfix (HRS-3326): the hour axis must
 * describe the rendered day columns, not the hour the grid SHOULD have --
 * routes can reach the view without a rebuild (home route-card pass-through
 * of stored `isRefactorRoute` days, localStorage restore), where the stored
 * grid may differ from the effective rule and the row counts would diverge.
 */
export function getRenderedGridStartHour(days: any[]): number {
  const candidates: number[] = [];

  (days || []).forEach((day: any) => {
    const firstSlotHour = parseGridStartHour(day?.points?.[0]?.start);
    if (firstSlotHour !== null)
      candidates.push(firstSlotHour);
  });

  if (candidates.length === 0)
    return DEFAULT_GRID_START_HOUR;

  return Math.min(...candidates);
}

/**
 * Generates the timeline hour-axis labels from a start hour to midnight
 * (inclusive, shown as "12 AM"), in the legacy 12-hour format. For
 * startHour = 9 it yields exactly the historical TIME_STRINGS sequence
 * ("9 AM" ... "12 AM").
 */
export function getHourAxisLabels(startHour: number): string[] {
  const labels: string[] = [];
  for (let hour = startHour; hour <= MIDNIGHT_END_HOUR; hour++)
    labels.push(formatHourLabel(hour));

  return labels;
}

function parseGridStartHour(value: unknown): number | null {
  if (value === null || value === undefined)
    return null;

  if (typeof value === 'number')
    return toValidGridStartHour(value);

  if (typeof value === 'string')
    return toValidGridStartHour(parseInt(value, 10)); // "07:00" -> 7

  if (dayjs.isDayjs(value))
    return value.isValid() ? toValidGridStartHour(value.hour()) : null;

  return null;
}

function toValidGridStartHour(hour: number): number | null {
  if (!Number.isFinite(hour))
    return null;

  const flooredHour = Math.floor(hour);
  return flooredHour >= MIN_GRID_START_HOUR && flooredHour <= MAX_GRID_START_HOUR ? flooredHour : null;
}

function formatHourLabel(hour: number): string {
  const hourOfDay = hour % 24; // 24 -> 0 (midnight)
  const suffix = hourOfDay < 12 ? 'AM' : 'PM';
  const hour12 = hourOfDay % 12 === 0 ? 12 : hourOfDay % 12;
  return `${hour12} ${suffix}`;
}
